import type { ApiNotAuthorized, LiveRefused, LiveStreamSegment } from "@hobbyshopify/backend-client";
import type { AsyncResult } from "operation-result.js";
import { BaseController, Rx, type RxList, type SubtreeModel } from "subtree.js/core";
import type { AppDeps } from "../../../../app-deps";
import { cancelBreak, removeSegment, reorderSegments, setCurrentSegment } from "../../../../core/ops/live-ops";
import { errorText } from "../../../../core/primitives/errors";
import { movedPlanned } from "../../../../core/primitives/lineup";
import { refundsFailedText } from "../../../../core/primitives/live";

export type LineupMove = "screen" | "remove" | "move" | "cancel";

export class LineupState {
  /** The one call in flight, and which row it belongs to. */
  readonly busy = new Rx<{ id: string; move: LineupMove } | null>(null);
  /** The last refusal, kept on its row until the next move. */
  readonly failure = new Rx<{ id: string; message: string } | null>(null);
}

export abstract class LineupActions {
  abstract putOnScreen(segmentId: string): Promise<void>;
  abstract remove(segmentId: string): Promise<void>;
  abstract move(segmentId: string, by: -1 | 1): Promise<void>;
  abstract cancelBreak(segmentId: string, breakId: string): Promise<void>;
  abstract addMore(): void;
}

export type LineupDeps = Pick<AppDeps, "client" | "authHandler">;

export interface LineupHost {
  streamId: string;
  segments: RxList<LiveStreamSegment>;
  /** The line-up changed: the stream detail is fetched again. */
  onChanged(): void;
  /** Opens the picker to add boxes or other segments. */
  addMore(): void;
}

/** The stream's line-up, one move at a time per row; every move reloads the stream. */
export class LineupController extends BaseController implements LineupActions {
  readonly state = new LineupState();

  constructor(
    subtree: SubtreeModel,
    readonly deps: LineupDeps,
    readonly host: LineupHost,
  ) {
    super();
    subtree.put(LineupState, this.state);
    subtree.put(LineupActions, this);
  }

  putOnScreen = async (segmentId: string) => {
    await this.#run(segmentId, "screen", () =>
      setCurrentSegment({ ...this.deps, streamId: this.host.streamId, segmentId }),
    );
  };

  remove = async (segmentId: string) => {
    await this.#run(segmentId, "remove", () =>
      removeSegment({ ...this.deps, streamId: this.host.streamId, segmentId }),
    );
  };

  move = async (segmentId: string, by: -1 | 1) => {
    const order = movedPlanned(this.host.segments.value, segmentId, by);
    if (!order) return;
    await this.#run(segmentId, "move", () =>
      reorderSegments({ ...this.deps, streamId: this.host.streamId, orderedSegmentIds: order }),
    );
  };

  cancelBreak = async (segmentId: string, breakId: string) => {
    let refundsFailed = 0;
    await this.#run(segmentId, "cancel", async () => {
      const r = await cancelBreak({ ...this.deps, breakId });
      if (r.ok) refundsFailed = r.value.refundsFailed;
      return r;
    });
    if (!this.isDisposed && refundsFailed > 0) {
      this.state.failure.value = { id: segmentId, message: refundsFailedText(refundsFailed) };
    }
  };

  addMore = () => {
    this.host.addMore();
  };

  async #run(
    id: string,
    move: LineupMove,
    call: () => AsyncResult<unknown, ApiNotAuthorized | LiveRefused>,
  ): Promise<void> {
    if (this.state.busy.value) return;
    this.state.busy.value = { id, move };
    this.state.failure.value = null;
    const r = await call();
    if (this.isDisposed) return;
    this.state.busy.value = null;
    if (!r.ok) {
      this.state.failure.value = { id, message: errorText(r.errors) };
      return;
    }
    this.host.onChanged();
  }
}
